/**
 * Opportunités d'aides — bandeau du dossier (#359 · Agent Aides Complémentaires)
 *
 * ⚠️ C'EST ICI QUE LA VALEUR MÉTIER SE VOIT.
 * « Ce dossier est éligible à N aides non sollicitées. » Avec, pour chacune :
 * laquelle, pourquoi elle est mobilisable, et ce qui manque le cas échéant.
 *
 * FORMULÉ POUR UN OPÉRATEUR, PAS POUR UN DÉVELOPPEUR. Aucun code de règle
 * affiché sans son libellé, aucune valeur brute sans ce qu'elle signifie.
 *
 * LECTURE SEULE À L'OUVERTURE. Le GET n'appelle que l'observation : consulter
 * un dossier ne produit aucun verdict et ne fausse aucun indicateur. Le bouton
 * « Réévaluer » est le seul chemin qui en produit un, et il est explicite.
 *
 * TROIS ÉTATS DISTINCTS, JAMAIS CONFONDUS :
 *   opportunités  aides démontrées mobilisables et non demandées → à solliciter
 *   à compléter   éligibilité INDÉTERMINÉE, faute de données → à renseigner
 *   conforme      tout ce qui est mobilisable a été demandé
 * Un dossier « à compléter » n'est pas un dossier fautif : c'est un dossier
 * dont on ne sait pas encore. La nuance est tout le sujet.
 *
 * CHARTE — zéro couleur en dur, paires `--oh-stat-{c}-bg` / `-text`, dual mode.
 */
import { useCallback, useEffect, useState } from "react";
import {
  AlertCircle, CheckCircle2, ChevronDown, ChevronUp, HelpCircle,
  Loader2, RefreshCw, Sparkles,
} from "lucide-react";
import { API, getAuthHeaders, formatDate } from "./helpers";

const fond = (c) => `var(--oh-stat-${c}-bg)`;
const texte = (c) => `var(--oh-stat-${c}-text)`;
const bord = (c) => `var(--oh-stat-${c}-border)`;

/** Les cinq verdicts, dits en français d'opérateur. */
const VERDICT = {
  conforme: { label: "Toutes les aides mobilisables ont été demandées", couleur: "green", Icone: CheckCircle2 },
  non_conforme: { label: "Des aides mobilisables n'ont pas été demandées", couleur: "amber", Icone: Sparkles },
  indetermine: { label: "Impossible de conclure — il manque des données", couleur: "blue", Icone: HelpCircle },
  non_applicable: { label: "Ce dossier n'entre pas dans le champ", couleur: "slate", Icone: CheckCircle2 },
  erreur_technique: { label: "Le contrôle n'a pas pu s'exécuter", couleur: "red", Icone: AlertCircle },
};

function Puce({ couleur, children }) {
  return (
    <span style={{
      background: fond(couleur), color: texte(couleur), borderRadius: 8,
      padding: "2px 8px", fontSize: 12, whiteSpace: "nowrap",
    }}>
      {children}
    </span>
  );
}

/** Les trois sens de décision. Aucun défaut : l'opérateur choisit explicitement. */
const SENS = [
  { cle: "acceptee", label: "Solliciter", couleur: "green", aide: "on demande cette aide" },
  { cle: "differee", label: "Différer", couleur: "blue", aide: "pas maintenant" },
  { cle: "rejetee", label: "Écarter", couleur: "red", aide: "on ne la demande pas — motif requis" },
];

const LIBELLE_SENS = {
  acceptee: "à solliciter", differee: "différée", rejetee: "écartée",
};

/** Les trois boutons de décision. ⚠️ Un rejet EXIGE un motif — le back refuse
 *  sans, et l'écran le demande avant d'envoyer plutôt que de faire échouer. */
function BoutonsDecision({ onDecider, occupe }) {
  const [motif, setMotif] = useState("");
  const [demandeMotif, setDemandeMotif] = useState(false);

  return (
    <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {SENS.map((s) => (
          <button
            key={s.cle}
            type="button"
            title={s.aide}
            disabled={occupe}
            onClick={() => (s.cle === "rejetee" ? setDemandeMotif(true) : onDecider(s.cle, ""))}
            style={{
              height: 36, padding: "0 12px", borderRadius: 8,
              cursor: occupe ? "not-allowed" : "pointer",
              background: "transparent", color: texte(s.couleur),
              border: `1px solid ${bord(s.couleur)}`,
            }}
          >
            {s.label}
          </button>
        ))}
      </div>
      {demandeMotif && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            placeholder="Motif de l'écartement (obligatoire)"
            aria-label="Motif de l'écartement"
            style={{
              flex: 1, minWidth: 220, height: 36, borderRadius: 8, padding: "0 12px",
              background: "var(--oh-bg)", color: "var(--oh-text)",
              border: "1px solid var(--oh-border)",
            }}
          />
          <button
            type="button"
            disabled={occupe || !motif.trim()}
            onClick={() => { onDecider("rejetee", motif.trim()); setDemandeMotif(false); setMotif(""); }}
            style={{
              height: 36, padding: "0 12px", borderRadius: 8,
              cursor: motif.trim() ? "pointer" : "not-allowed",
              background: motif.trim() ? "var(--oh-accent-solid)" : "transparent",
              color: motif.trim() ? "var(--oh-accent-contrast)" : "var(--oh-text-muted)",
              border: "1px solid var(--oh-border)",
            }}
          >
            Confirmer l'écartement
          </button>
        </div>
      )}
    </div>
  );
}

/** Une aide : ce qu'elle est, et le détail condition par condition. */
function LigneAide({ aide, couleur, ouverteParDefaut = false, onDecider, occupe }) {
  const [ouverte, setOuverte] = useState(ouverteParDefaut);
  return (
    <div style={{
      border: `0.5px solid ${bord(couleur)}`, borderRadius: 8,
      background: "var(--oh-bg-elevated)", padding: 12,
    }}>
      <button
        type="button"
        onClick={() => setOuverte((v) => !v)}
        aria-expanded={ouverte}
        style={{
          display: "flex", alignItems: "flex-start", gap: 12, width: "100%",
          background: "transparent", border: "none", cursor: "pointer",
          textAlign: "left", padding: 0,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: "var(--oh-text)", lineHeight: 1.6 }}>{aide.nom}</div>
          <div style={{ color: "var(--oh-text-muted)", fontSize: 13, lineHeight: 1.6 }}>
            {aide.organisme}
            {aide.montant_max ? ` · jusqu'à ${Math.round(aide.montant_max).toLocaleString("fr-FR")} €` : ""}
          </div>
          <div style={{ color: texte(couleur), fontSize: 13, marginTop: 4, lineHeight: 1.6 }}>
            {aide.motif}
          </div>
        </div>
        {ouverte
          ? <ChevronUp size={16} style={{ color: "var(--oh-text-muted)", flexShrink: 0 }} />
          : <ChevronDown size={16} style={{ color: "var(--oh-text-muted)", flexShrink: 0 }} />}
      </button>

      {ouverte && (
        <div style={{ marginTop: 12, display: "grid", gap: 6 }}>
          {(aide.conditions || []).map((c, i) => {
            const couleurCondition =
              c.resultat === "vrai" ? "green" : c.resultat === "faux" ? "red" : "blue";
            const dit =
              c.resultat === "vrai" ? "satisfaite"
              : c.resultat === "faux" ? "non satisfaite"
              : "non vérifiable";
            return (
              <div key={i} style={{
                display: "flex", justifyContent: "space-between", gap: 12,
                flexWrap: "wrap", fontSize: 13, lineHeight: 1.6,
                color: "var(--oh-text-secondary)",
              }}>
                <span>{c.condition}</span>
                <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {c.lu !== undefined && c.lu !== null && (
                    <span style={{ color: "var(--oh-text-muted)" }}>
                      au dossier : {String(c.lu)}
                    </span>
                  )}
                  <Puce couleur={couleurCondition}>{dit}</Puce>
                </span>
              </div>
            );
          })}
          {aide.decision && (
            <div style={{
              background: fond("slate"), color: texte("slate"),
              borderRadius: 8, padding: 8, fontSize: 13, lineHeight: 1.6,
            }}>
              {/* ⚠️ ÉTAPE 9 — qui, quand, pourquoi. Une décision anonyme ne se
                  distinguerait pas d'une sortie machine. */}
              Décidée « {LIBELLE_SENS[aide.decision.sens] || aide.decision.sens} » par{" "}
              {aide.decision.auteur_nom || aide.decision.auteur} le{" "}
              {formatDate(aide.decision.decide_le)}
              {aide.decision.motif ? ` — ${aide.decision.motif}` : ""}
            </div>
          )}
          {onDecider && !aide.decision && (
            <BoutonsDecision
              occupe={occupe}
              onDecider={(sens, motif) => onDecider(aide, sens, motif)}
            />
          )}
          {(aide.manquants || []).length > 0 && (
            <div style={{
              background: fond("blue"), color: texte("blue"),
              borderRadius: 8, padding: 8, fontSize: 13, lineHeight: 1.6,
            }}>
              À renseigner pour trancher :{" "}
              {aide.manquants.map((m) => m.replace("beneficiaire.", "")).join(", ")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function OpportunitesAidesBandeau({ dossierId, tenantId }) {
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [reevaluation, setReevaluation] = useState(false);
  const [decisionEnCours, setDecisionEnCours] = useState(false);
  const [historiqueOuvert, setHistoriqueOuvert] = useState(false);
  const [erreur, setErreur] = useState(null);

  const headers = getAuthHeaders(tenantId);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const res = await fetch(`${API}/api/hub-ia/dossiers/${dossierId}/opportunites`, {
        headers, credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDonnees(await res.json());
    } catch (e) {
      setErreur(e.message || "Erreur de chargement");
      setDonnees(null);
    } finally {
      setChargement(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dossierId]);

  useEffect(() => { charger(); }, [charger]);

  async function reevaluer() {
    setReevaluation(true);
    try {
      await fetch(`${API}/api/hub-ia/dossiers/${dossierId}/opportunites/evaluer`, {
        method: "POST", headers: { ...headers, "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ par: "ecran-dossier" }),
      });
      await charger();
    } catch (e) {
      setErreur(e.message);
    } finally {
      setReevaluation(false);
    }
  }

  async function decider(aide, sens, motif) {
    setDecisionEnCours(true);
    setErreur(null);
    try {
      const res = await fetch(
        `${API}/api/hub-ia/dossiers/${dossierId}/opportunites/${aide.aide_id}/decision`,
        {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ sens, motif, aide_nom: aide.nom }),
        }
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${res.status}`);
      }
      await charger();
    } catch (e) {
      setErreur(e.message);
    } finally {
      setDecisionEnCours(false);
    }
  }

  if (chargement) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 16 }}>
        <Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--oh-accent-text)" }} />
        <span style={{ color: "var(--oh-text-muted)", fontSize: 13 }}>
          Analyse des aides mobilisables…
        </span>
      </div>
    );
  }

  if (erreur) {
    return (
      <div style={{
        background: fond("red"), color: texte("red"), border: `0.5px solid ${bord("red")}`,
        borderRadius: 12, padding: 16, lineHeight: 1.6,
      }}>
        Les opportunités n'ont pas pu être chargées ({erreur}).
      </div>
    );
  }

  // L'agent est désactivé, ou le dossier est hors de son champ : on le DIT,
  // plutôt que d'afficher un bandeau vide qui se lirait « aucune opportunité ».
  if (!donnees?.observe) {
    return (
      <div style={{
        background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)",
        borderRadius: 12, padding: 16, color: "var(--oh-text-secondary)",
        fontSize: 13, lineHeight: 1.6,
      }}>
        Analyse des aides indisponible pour ce dossier — {donnees?.raison || "raison inconnue"}.
      </div>
    );
  }

  const opportunites = donnees.opportunites || [];
  const aCompleter = donnees.a_completer || [];
  const dernier = donnees.dernier_verdict;
  const etat = VERDICT[dernier?.resultat] || null;

  const couleurEntete = opportunites.length > 0 ? "amber"
    : aCompleter.length > 0 ? "blue" : "green";
  const Icone = opportunites.length > 0 ? Sparkles
    : aCompleter.length > 0 ? HelpCircle : CheckCircle2;

  return (
    <div style={{
      background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)",
      borderRadius: 12, padding: 16,
    }} data-testid="opportunites-aides-bandeau">
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div style={{
          background: fond(couleurEntete), color: texte(couleurEntete),
          borderRadius: 8, padding: 8, display: "flex", alignItems: "center",
        }}>
          <Icone size={18} />
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          {/* ⚠️ La phrase que l'opérateur doit lire, telle quelle. */}
          <div style={{ color: "var(--oh-text)", fontWeight: 500, lineHeight: 1.6 }}>
            {opportunites.length > 0
              ? `Ce dossier est éligible à ${opportunites.length} aide${opportunites.length > 1 ? "s" : ""} non sollicitée${opportunites.length > 1 ? "s" : ""}.`
              : aCompleter.length > 0
                ? "Aucune aide non sollicitée détectée, mais des données manquent pour conclure."
                : "Toutes les aides mobilisables pour ce dossier ont été demandées."}
          </div>
          <div style={{ color: "var(--oh-text-muted)", fontSize: 13, lineHeight: 1.6 }}>
            {donnees.synthese?.catalogue_total ?? 0} aide(s) au catalogue ·{" "}
            {donnees.synthese?.nb_sollicitees ?? 0} déjà sollicitée(s)
            {dernier ? ` · dernier contrôle le ${formatDate(dernier.horodatage)}` : ""}
          </div>
        </div>
        <button
          type="button"
          onClick={reevaluer}
          disabled={reevaluation}
          aria-label="Réévaluer les aides mobilisables pour ce dossier"
          style={{
            height: 36, padding: "0 12px", borderRadius: 8,
            cursor: reevaluation ? "not-allowed" : "pointer",
            background: "transparent", color: "var(--oh-accent-text)",
            border: "1px solid var(--oh-accent)",
            display: "flex", alignItems: "center", gap: 8,
          }}
        >
          <RefreshCw size={14} className={reevaluation ? "animate-spin" : ""} />
          Réévaluer
        </button>
      </div>

      {etat && (
        <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Puce couleur={etat.couleur}>{etat.label}</Puce>
          <span style={{ color: "var(--oh-text-muted)", fontSize: 12 }}>
            règle {dernier.regle_code} v{dernier.regle_version} · agent v{dernier.agent_version}
          </span>
        </div>
      )}

      {opportunites.length > 0 && (
        <div style={{ marginTop: 16, display: "grid", gap: 8 }}>
          <div style={{ color: "var(--oh-text-secondary)", fontSize: 13 }}>
            À solliciter — conditions déjà satisfaites
          </div>
          {opportunites.map((a) => (
            <LigneAide
              key={a.aide_id} aide={a} couleur="amber"
              ouverteParDefaut={opportunites.length === 1}
              onDecider={decider} occupe={decisionEnCours}
            />
          ))}
        </div>
      )}

      {aCompleter.length > 0 && (
        <div style={{ marginTop: 16, display: "grid", gap: 8 }}>
          <div style={{ color: "var(--oh-text-secondary)", fontSize: 13 }}>
            À compléter — l'éligibilité ne peut pas être tranchée
          </div>
          {aCompleter.map((a) => (
            <LigneAide key={a.aide_id} aide={a} couleur="blue" />
          ))}
        </div>
      )}
      {(donnees.decidees || []).length > 0 && (
        <div style={{ marginTop: 16, display: "grid", gap: 8 }}>
          <div style={{ color: "var(--oh-text-secondary)", fontSize: 13 }}>
            Déjà décidé — ni une opportunité, ni un oubli
          </div>
          {donnees.decidees.map((a) => (
            <LigneAide key={a.aide_id} aide={a} couleur="slate" />
          ))}
        </div>
      )}

      {(donnees.historique_decisions || []).length > 0 && (
        <div style={{ marginTop: 16 }}>
          <button
            type="button"
            onClick={() => setHistoriqueOuvert((v) => !v)}
            aria-expanded={historiqueOuvert}
            style={{
              height: 36, padding: "0 12px", borderRadius: 8, cursor: "pointer",
              background: "transparent", color: "var(--oh-text-secondary)",
              border: "1px solid var(--oh-border)",
              display: "flex", alignItems: "center", gap: 8,
            }}
          >
            {historiqueOuvert ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            Historique des décisions ({donnees.historique_decisions.length})
          </button>
          {historiqueOuvert && (
            <div style={{ marginTop: 8, display: "grid", gap: 6 }}>
              {/* Append-only : changer d'avis empile, n'écrase pas. */}
              {donnees.historique_decisions.map((h) => (
                <div key={h.decision_id} style={{
                  fontSize: 13, lineHeight: 1.6, color: "var(--oh-text-secondary)",
                  borderLeft: `2px solid ${bord("slate")}`, paddingLeft: 8,
                }}>
                  <span style={{ color: "var(--oh-text)" }}>
                    {h.aide_nom || h.aide_id}
                  </span>{" "}
                  — {LIBELLE_SENS[h.sens] || h.sens} par {h.auteur_nom || h.auteur},{" "}
                  {formatDate(h.decide_le)}
                  {h.motif ? ` · ${h.motif}` : ""}
                  {h.agent_version ? ` · agent v${h.agent_version}` : ""}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
}

export default OpportunitesAidesBandeau;
